// Minimal toast notification system.
//
// Why a custom one and not a library: zero new dependencies, ~80 lines,
// matches our Telegram-inspired palette exactly, and we only need three
// variants (success / error / info). Native browser dialogs (alert/confirm)
// are jarring and block the event loop; toasts feel modern and don't.
//
// Usage:
//   const toast = useToast();
//   toast.success("Saved");
//   toast.error(e.message);
//   toast.info("Linking code copied");
//   toast.confirm("Delete this post?").then(ok => { if (ok) ... });
//
// Provider is added once near the root of <App>. The bare components do
// not render anything outside <ToastViewport>; tree consumers just call
// the hook.
import { createContext, useContext, useState, useCallback, useRef } from "react";
import { T, T_NEW } from "../theme.js";
import { Btn, Inp } from "./ui.jsx";

const ToastContext = createContext(null);

let nextId = 1;

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const [confirmState, setConfirmState] = useState(null); // { message, resolve }
  const [promptState, setPromptState] = useState(null);   // { message, placeholder, defaultValue, resolve }
  const timersRef = useRef(new Map());

  const dismiss = useCallback((id) => {
    setToasts((cur) => cur.filter((t) => t.id !== id));
    const handle = timersRef.current.get(id);
    if (handle) { clearTimeout(handle); timersRef.current.delete(id); }
  }, []);

  const push = useCallback((variant, message, ttlMs = 4000) => {
    const id = nextId++;
    setToasts((cur) => [...cur, { id, variant, message }]);
    const handle = setTimeout(() => dismiss(id), ttlMs);
    timersRef.current.set(id, handle);
    return id;
  }, [dismiss]);

  const api = {
    success: (msg) => push("success", msg),
    error:   (msg) => push("error",   msg, 6000), // errors linger a bit longer
    info:    (msg) => push("info",    msg),
    confirm: (msg) => new Promise((resolve) => setConfirmState({ message: msg, resolve })),
    /**
     * Modal text-input prompt. Resolves to the entered string, or null if
     * the user cancels. Drop-in replacement for window.prompt().
     *
     * opts: { placeholder?, defaultValue?, multiline? }
     */
    prompt: (msg, opts = {}) =>
      new Promise((resolve) => setPromptState({ message: msg, ...opts, resolve })),
  };

  return (
    <ToastContext.Provider value={api}>
      {children}
      <ToastViewport toasts={toasts} onDismiss={dismiss} />
      {confirmState && (
        <ConfirmDialog
          message={confirmState.message}
          onResult={(ok) => { confirmState.resolve(ok); setConfirmState(null); }}
        />
      )}
      {promptState && (
        <PromptDialog
          {...promptState}
          onResult={(value) => { promptState.resolve(value); setPromptState(null); }}
        />
      )}
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    // Safe fallback so a component used outside the provider doesn't crash.
    // Logs once so dev notices the missing provider.
    if (!useToast._warned) { console.warn("[toast] useToast used outside ToastProvider"); useToast._warned = true; }
    return {
      success: console.log,
      error:   console.error,
      info:    console.log,
      confirm: (m) => Promise.resolve(window.confirm(m)),
      prompt:  (m) => Promise.resolve(window.prompt(m)),
    };
  }
  return ctx;
}

function ToastViewport({ toasts, onDismiss }) {
  return (
    <div style={{
      position: "fixed", bottom: 18, right: 18, zIndex: 200, display: "flex",
      flexDirection: "column-reverse", gap: 10, maxWidth: 400, pointerEvents: "none",
    }}>
      {toasts.map((t) => {
        // Editorial tone mapping — each variant gets a different stamp
        // color + tag label. Success = bottle green, error = danger,
        // info = persimmon.
        const tone = t.variant === "success" ? { color: T_NEW.bottle, label: "OK" }
                   : t.variant === "error"   ? { color: T_NEW.danger, label: "Ошибка" }
                   :                            { color: T_NEW.persimmon, label: "Инфо" };
        return (
          <div key={t.id} onClick={() => onDismiss(t.id)}
               role="status" aria-live="polite"
               style={{
                 pointerEvents: "auto", cursor: "pointer", background: T_NEW.paperRaised,
                 color: T_NEW.ink, padding: "14px 16px 14px 18px", borderRadius: T_NEW.r2,
                 borderLeft: `4px solid ${tone.color}`, fontSize: T_NEW.bodySm,
                 lineHeight: 1.45,
                 boxShadow: T_NEW.shadowModal,
                 animation: "toastIn 0.18s ease-out",
                 wordBreak: "break-word",
                 fontFamily: T_NEW.fontSans,
                 display: "flex", flexDirection: "column", gap: 4,
               }}>
            <span style={{ fontSize: T_NEW.micro, color: tone.color, fontWeight: 700,
                            fontFamily: T_NEW.fontMono, textTransform: "uppercase",
                            letterSpacing: T_NEW.monoTrack }}>
              {tone.label}
            </span>
            <span>{t.message}</span>
          </div>
        );
      })}
      <style>{`@keyframes toastIn { from { opacity:0; transform:translateY(6px); } to { opacity:1; transform:none; } }`}</style>
    </div>
  );
}

// Text-input prompt. Resolves to a string on Submit / Enter, null on
// Cancel / Escape / overlay click. Multiline becomes a textarea.
function PromptDialog({ message, placeholder, defaultValue, multiline, onResult }) {
  const [value, setValue] = useState(defaultValue ?? "");
  const submit = () => onResult(value);
  const cancel = () => onResult(null);
  return (
    <div onClick={cancel}
         style={{ position: "fixed", inset: 0, background: T_NEW.overlay || "rgba(20,17,13,0.55)",
                  display: "flex", alignItems: "center", justifyContent: "center", zIndex: 250 }}>
      <div onClick={(e) => e.stopPropagation()}
           style={{ background: T_NEW.paperRaised, borderRadius: T_NEW.r3, padding: 26,
                    width: 440, maxWidth: "92vw", boxShadow: T_NEW.shadowModal,
                    border: `1px solid ${T_NEW.ink12}`, fontFamily: T_NEW.fontSans }}>
          <div style={{ fontSize: T_NEW.bodyLg, color: T_NEW.ink, lineHeight: 1.4,
                        marginBottom: 16, fontFamily: T_NEW.fontSerif }}>
            {message}
          </div>
        {multiline ? (
          <textarea
            autoFocus
            value={value}
            placeholder={placeholder}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Escape") cancel(); }}
            style={{ width: "100%", background: T_NEW.paper, color: T_NEW.ink,
                     border: `1px solid ${T_NEW.ink12}`, padding: "12px 14px",
                     borderRadius: T_NEW.r1, fontSize: 14, minHeight: 96,
                     resize: "vertical", fontFamily: T_NEW.fontSans,
                     marginBottom: 18, lineHeight: 1.5 }}
          />
        ) : (
          <Inp value={value} onChange={setValue} placeholder={placeholder} autoFocus />
        )}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
          <Btn variant="ghost" onClick={cancel}>Отмена</Btn>
          <Btn onClick={submit}>OK</Btn>
        </div>
      </div>
    </div>
  );
}

// Non-blocking confirm — modal-ish overlay returning the user's choice via
// the promise. Mostly here to retire window.confirm() calls so the visual
// language stays consistent.
function ConfirmDialog({ message, onResult }) {
  return (
    <div onClick={() => onResult(false)}
         style={{ position: "fixed", inset: 0, background: T_NEW.overlay || "rgba(20,17,13,0.55)",
                  display: "flex", alignItems: "center", justifyContent: "center", zIndex: 250 }}>
      <div onClick={(e) => e.stopPropagation()}
           style={{ background: T_NEW.paperRaised, borderRadius: T_NEW.r3, padding: 26,
                    width: 400, maxWidth: "92vw", boxShadow: T_NEW.shadowModal,
                    border: `1px solid ${T_NEW.ink12}`, fontFamily: T_NEW.fontSans }}>
        {/* Mono "подтвердите" tag above the message, like a notarized
            stamp — sets expectation that this is a deliberate action. */}
        <div style={{ fontFamily: T_NEW.fontMono, fontSize: T_NEW.micro,
                      color: T_NEW.persimmon, textTransform: "uppercase",
                      letterSpacing: T_NEW.monoTrack, fontWeight: 700, marginBottom: 10 }}>
          Подтверждение
        </div>
        <div style={{ fontSize: T_NEW.bodyLg, color: T_NEW.ink, lineHeight: 1.45,
                      marginBottom: 22, fontFamily: T_NEW.fontSerif }}>
          {message}
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
          <Btn variant="ghost" onClick={() => onResult(false)}>Отмена</Btn>
          <Btn variant="danger" onClick={() => onResult(true)}>Да, подтвердить</Btn>
        </div>
      </div>
    </div>
  );
}
